import "./App.scss";

import { store } from "./redux/store";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import AppRoutes from "routes/AppRoutes";
import Toaster from "components/atoms/Toaster";
import Footer from "components/templates/Footer";
import AppModal from "components/templates/AppModal";
import AdvanceModal from "components/templates/AdvanceModal";
import ScrollTop from "components/templates/ScrollTop";
import ErrorBoundary from "components/atoms/ErrorBoundary";
import RegisterAppDispatch from "components/atoms/RegisterAppDispatch";
import PageViewTracker from "components/analytics/PageViewTracker";

import createCache from "@emotion/cache";
import { SnackbarProvider } from "notistack";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider } from "@mui/material/styles";
import NavBar from "components/templates/NavBar";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { useTranslation } from "react-i18next";
import { createTheme } from "@mui/material";
import { useLayoutEffect, useState, useEffect } from "react";
import mixpanel from "mixpanel-browser";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LicenseInfo } from "@mui/x-license-pro";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY || "");
// import SocialPixels from "socialPixels";

// import ar from "date-fns/locale/ar";
// import en from "date-fns/locale/en-US";

declare module "@mui/material/styles" {
  interface Theme {
    status: {
      success: object;
      warning: object;
    };
    customColor: {
      secondary: object;
    };
  }
  interface ThemeOptions {
    status?: {
      success?: object;
      warning?: object;
    };
    customColor: {
      secondary: object;
    };
  }
}

// const stripePromise = loadStripe(config.STRIPE_KEY);
const cache = createCache({ key: "css", prepend: true });

const cacheRtl = createCache({
  key: "muirtl",
  //@ts-ignore
  stylisPlugins: [prefixer, rtlPlugin],
});

// Initialize Mixpanel
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

if (!MIXPANEL_TOKEN) {
  console.error("Mixpanel token is not defined in environment variables");
} else {
  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: true, // Force debug mode to see all logs
      track_pageview: true,
      persistence: "localStorage",
      api_host: "https://api.mixpanel.com", // Changed to main API endpoint
      test: true, // Enable test mode
    });

    // Configure Mixpanel to handle errors
    mixpanel.set_config({
      debug: true,
      track_pageview: true,
      persistence: "localStorage",
      api_host: "https://api.mixpanel.com",
      test: true,
    });

    // Add error handler
    mixpanel.register({
      error_handler: (error: Error) => {
        console.error("Mixpanel Error:", error);
      },
    });

    console.log("Mixpanel initialization completed");
  } catch (error) {
    console.error("Error initializing Mixpanel:", error);
  }
}

// Add retry logic for failed events
const trackWithRetry = (
  eventName: string,
  properties: Record<string, any>,
  maxRetries = 3,
) => {
  let retries = 0;

  const attemptTrack = () => {
    try {
      mixpanel.track(eventName, properties, {
        transport: "xhr",
        send_immediately: true,
      });
      console.log(`Successfully tracked ${eventName}`);
    } catch (error) {
      console.error(`Error tracking ${eventName}:`, error);
      if (retries < maxRetries) {
        retries++;
        setTimeout(attemptTrack, 1000 * retries); // Exponential backoff
      }
    }
  };

  attemptTrack();
};

export default function App() {
  const { i18n } = useTranslation();

  const theme = createTheme({
    status: {
      success: {
        color: "rgba(84, 214, 44, 0.16)",
        textContrast: "rgb(34, 154, 22)",
      },
      warning: {
        color: "rgba(255, 193, 7, 0.16)",
        textContrast: "rgb(183, 129, 3)",
      },
    },
    customColor: {
      secondary: {
        color: "rgba(0, 0, 0, 0.53)",
      },
    },
    palette: {
      primary: {
        light: "#FFCD00",
        main: "#FFCD00",
        dark: "#FFCD00",
        contrastText: "#520A76",
      },
      secondary: {
        main: "#520A76",
        light: "#520A76",
        dark: "#520A76",
        contrastText: "#FFFFFF",
      },
      success: {
        main: "rgb(34, 154, 22)",
        light: "rgba(84, 214, 44, 0.16)",
        dark: "#1b5e20",
        // contrastText: "rgb(34, 154, 22)",
      },
      warning: {
        main: "rgb(183, 129, 3)",
        light: "rgba(255, 193, 7, 0.16)",
        dark: "#e65100",
        // contrastText: "rgb(183, 129, 3)"
      },
      error: {
        main: "rgb(183, 33, 54)",
        light: "rgba(255, 72, 66, 0.16)",
        dark: "#c62828",
        // contrastText: "rgb(183, 33, 54)",
      },
      info: {
        main: "#520A76",
        light: "#520A76",
        dark: "#520A76",
        contrastText: "#FFFFFF",
      },
    },
    typography: {
      fontFamily: i18n.language === "ar" ? "arabic" : "arabic",
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1480,
        xl: 1800,
      },
    },
  });

  // Get the browser's language
  const browserLang = navigator.language.split("-")[0]; // It will give 'en', 'ar', etc.
  console.log("browserLang", browserLang);

  // Check if the browser language is either 'en' or 'ar', otherwise default to 'en'
  const defaultLang =
    browserLang === "en" || browserLang === "ar" ? browserLang : "en";
  // const storedLang = localStorage.getItem("lang") || "en";
  const [lang] = useState(
    window.location.pathname.split("/")[1] ?? defaultLang,
  );

  // Sync i18n whenever lang changes
  useLayoutEffect(() => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  }, [i18n, lang]);

  // If the user initially hits "/", or any path without "/en" or "/ar",
  // we can do a quick redirect on first load:
  // if (!/^\/(en|ar)/.test(window.location.pathname)) {
  //   window.location.replace(`/${lang}${window.location.pathname}${window.location.search}`);
  //   return null; // Prevent rendering until we redirect
  // }

  useLayoutEffect(() => {
    const path = window.location.pathname;
    if (
      path.includes("yelo-business-jeeny") ||
      path.includes(`/${defaultLang}/yelo-business-jeeny`)
    )
      return;

    if (!/^\/(en|ar)/.test(window.location.pathname) && defaultLang) {
      console.log("Redirecting to", lang, browserLang, defaultLang);
      window.location.replace(
        `/${defaultLang}${window.location.pathname}${window.location.search}`,
      );
    }
  }, [defaultLang]);

  // Add Mixpanel page view tracking with retry
  useEffect(() => {
    if (MIXPANEL_TOKEN) {
      trackWithRetry("Page View", {
        page: window.location.pathname,
        language: i18n.language,
        timestamp: Date.now(),
      });
    }
  }, [i18n.language]);

  return (
    <ErrorBoundary>
      <SnackbarProvider
        maxSnack={5}
        hideIconVariant
        preventDuplicate
        // anchorOrigin={{
        //   vertical: "bottom",
        //   horizontal: "right",
        // }}
        // iconVariant={{
        //   success: "✅",
        //   error: "✖️",
        //   warning: "⚠️",
        //   info: "ℹ️",
        // }}
      >
        <Toaster />
        {/* <SocialPixels /> */}
        <ErrorBoundary>
          {/* <Elements stripe={stripePromise}> */}
          <CacheProvider value={i18n.language === "ar" ? cacheRtl : cache}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider
                // locale={i18n.language === "ar" ? "ar-AR" : "en-EN"}
                dateAdapter={AdapterDayjs}
              >
                {/* <CssBaseline /> */}
                {/* <ScopedCssBaseline> */}
                <Provider store={store}>
                  <RegisterAppDispatch />
                  <BrowserRouter basename={`/${lang}`}>
                    <ErrorBoundary>
                      <NavBar />
                    </ErrorBoundary>
                    {/* <ErrorBoundary>
												<Cookies />``
											</ErrorBoundary> */}
                    <ErrorBoundary>
                      <ScrollTop />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <AppModal />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <AdvanceModal />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <PageViewTracker />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <AppRoutes />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <Footer />
                    </ErrorBoundary>
                  </BrowserRouter>
                </Provider>
                {/* </ScopedCssBaseline> */}
              </LocalizationProvider>
            </ThemeProvider>
          </CacheProvider>
          {/* </Elements> */}
        </ErrorBoundary>
      </SnackbarProvider>
    </ErrorBoundary>
  );
}
